import React, { useState, useEffect } from "react";

import RichText from "@cayuse/shared-react.components.form.inputs.rich-text";

import { FORM_EVENTS } from "components/FormResponse/hooks/useFormSave";

import FlowableErrorComponent from "../Common/ErrorComponent/FlowableErrorComponent";
import styles from "./styles.css";

const FlowableRichText = props => {
	const { config, Components } = props;
	const [ value, setValue ] = useState(config.value || "");
	const [ focused, setFocused ] = useState(false);
	const [ touched, setTouched ] = useState(false);

	const bem = (base, modifiers = {}) => {
		let className = base;
		Object.keys(modifiers).forEach(key => {
			if (modifiers[key]) {className += ` ${base}--${key}`;}
		});

		return className;
	};

	const { isRequired, extraSettings, customValidations = [], minLengthErrorMessage, maxLengthErrorMessage, placeholder } = config;

	const getPlainTextLength = html => {
		const tempElement = document.createElement("div");
		tempElement.innerHTML = html;

		return tempElement.textContent.trim().length;
	};

	const empty = isRequired && !getPlainTextLength(value);
	const minError = extraSettings?.minLength && getPlainTextLength(value) < extraSettings.minLength;
	const maxError = extraSettings?.maxLength && getPlainTextLength(value) > extraSettings.maxLength;
	const hasCustomValidationError = customValidations.some(validation => !validation.expression);
	const isValid = !empty && !minError && !maxError;

	const handleChange = content => {
		const text = content?.value || "";
		setValue(text);
		props.onChange(text);
	};

	const handleFocus = () => {
		if (!config.enabled) {
			return;
		}
		setFocused(true);
		setTouched(true);

		if (props.onEvent) {
			props.onEvent(FORM_EVENTS.FOCUS, { $path: config.$path }, {});
		}
	};
	const handleBlur = () => setFocused(false);

	const rows = extraSettings?.minRows;

	const richTextFormats = [
		"background",
		"bold",
		"color",
		"font",
		"code",
		"italic",
		"link",
		"size",
		"strike",
		"script",
		"underline",
		"blockquote",
		"header",
		"indent",
		"list",
		"align",
		"direction",
		"code-block",
		"formula",
	];

	useEffect(() => {
		if (config.value !== value) {
			setValue(config.value || "");
		}
	}, [ config.value ]);

	return (
		<div
			data-flw-id={config.id}
			className={bem("flowable-rich-text", { disabled: !config.enabled, focused })}
			style={{
				margin: "6px",
				width: "100%" }}
			role="application"
		>
			{!props.hideLabel && (
				<Components.label
					{...props}
					config={config}
					className={`${
						config.isRequired
							? "flw__component--required__label"
							: ""
					}`}
				/>
			)}
			<RichText
				className={`${styles.richTextInput} ${!config.enabled ? styles.disabled : ""} ${!isValid && config.enabled ? styles.error : ""}`}
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				value={value}
				style={{
					"--dynamic-min-height": `${rows * 1.8}em`,
				}}
				placeholder={placeholder}
				formats={richTextFormats}
				readOnly={!config.enabled}
			/>
			<Components.description
				{...props}
				className={bem("flowable-rich-text__description")}
			/>
			<div aria-live="polite">
				{(touched || focused) && (!isValid || hasCustomValidationError) && (
					<FlowableErrorComponent
						type={config.type}
						extraSettings={extraSettings}
						customValidations={customValidations}
						empty={empty}
						minError={minError}
						maxError={maxError}
						minLengthError={minLengthErrorMessage}
						maxLengthError={maxLengthErrorMessage}
					/>
				)}
			</div>
		</div>
	);
};

export default FlowableRichText;
